import CloseIcon from "@mui/icons-material/Close";
import { TYPOGRAPHY } from "components-redesign/styles";
import { makeStyles } from "components/mui-core";
import React from "react";

import {
  INLINE_ACTION_MAX_LABEL_LENGTH,
  SNACKBAR_BORDER_RADIUS,
  SNACKBAR_COLORS,
  SNACKBAR_ELEVATION,
  SNACKBAR_ON_COLOR,
  SNACKBAR_WIDTH,
} from "./constants";
import {
  SnackbarAction,
  SnackbarActionsPlacement,
  SnackbarVariant,
} from "./types";

const useStyles = makeStyles()({
  root: {
    boxSizing: "border-box",
    width: SNACKBAR_WIDTH,
    maxWidth: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: SNACKBAR_BORDER_RADIUS,
    boxShadow: SNACKBAR_ELEVATION,
    color: SNACKBAR_ON_COLOR,
    pointerEvents: "auto",
  },
  contentRow: {
    display: "flex",
    alignItems: "flex-start",
    minHeight: 48,
  },
  message: {
    ...TYPOGRAPHY.bodyMedium,
    flex: "1 1 auto",
    minWidth: 0,
    padding: "14px 0 14px 16px",
    wordBreak: "break-word",
    // Callers join lists of problems with "\n" and expect one line each.
    whiteSpace: "pre-line",
  },
  messageWithoutClose: {
    paddingRight: 16,
  },
  inlineActions: {
    display: "flex",
    alignItems: "center",
    alignSelf: "center",
    flexShrink: 0,
    padding: "4px 0",
  },
  actionsRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    padding: "0 8px 8px",
  },
  action: {
    ...TYPOGRAPHY.labelLarge,
    appearance: "none",
    background: "transparent",
    border: "none",
    borderRadius: 999,
    color: "inherit",
    cursor: "pointer",
    padding: "10px 12px",
    whiteSpace: "nowrap",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.12)",
    },
    "&:focus-visible": {
      outline: `2px solid ${SNACKBAR_ON_COLOR}`,
      outlineOffset: -2,
    },
  },
  close: {
    appearance: "none",
    background: "transparent",
    border: "none",
    color: "inherit",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: 48,
    height: 48,
    padding: 0,
    "&:hover": {
      opacity: 0.8,
    },
    "&:focus-visible": {
      outline: `2px solid ${SNACKBAR_ON_COLOR}`,
      outlineOffset: -4,
    },
  },
  closeIcon: {
    fontSize: 24,
  },
});

export type SnackbarProps = {
  variant: SnackbarVariant;
  message: React.ReactNode;
  actions?: SnackbarAction[];
  closable?: boolean;
  actionsPlacement?: SnackbarActionsPlacement;
  onClose?: () => void;
  className?: string;
  dataQa?: string;
};

/** Short single actions share the message row; longer or multiple ones wrap. */
export const shouldWrapActions = (
  actions: SnackbarAction[] | undefined,
  placement: SnackbarActionsPlacement = "auto"
): boolean => {
  if (!actions?.length) return false;
  if (placement === "inline") return false;
  if (placement === "below") return true;
  return (
    actions.length > 1 ||
    actions[0].label.length > INLINE_ACTION_MAX_LABEL_LENGTH
  );
};

function Snackbar({
  variant,
  message,
  actions,
  closable = true,
  actionsPlacement = "auto",
  onClose,
  className,
  dataQa = "snackbar",
}: SnackbarProps) {
  const { classes, cx } = useStyles();
  const wrapActions = shouldWrapActions(actions, actionsPlacement);
  const hasActions = !!actions?.length;

  const renderAction = (action: SnackbarAction) => (
    <button
      key={action.label}
      type="button"
      className={classes.action}
      data-qa={action.dataQa ?? `snackbar-action-${action.label}`}
      onClick={() => {
        action.onClick();
        if (action.closeOnClick !== false) onClose?.();
      }}>
      {action.label}
    </button>
  );

  return (
    <div
      className={cx(classes.root, className)}
      style={{ backgroundColor: SNACKBAR_COLORS[variant] }}
      role={variant === "error" ? "alert" : "status"}
      data-qa={dataQa}
      data-variant={variant}>
      <div className={classes.contentRow}>
        <div
          className={cx(classes.message, {
            [classes.messageWithoutClose]: !closable && !hasActions,
          })}>
          {message}
        </div>

        {hasActions && !wrapActions && (
          <div className={classes.inlineActions}>
            {actions?.map(renderAction)}
          </div>
        )}

        {closable && (
          <button
            type="button"
            className={classes.close}
            aria-label="Close notification"
            data-qa="snackbar-close"
            onClick={onClose}>
            <CloseIcon className={classes.closeIcon} />
          </button>
        )}
      </div>

      {hasActions && wrapActions && (
        <div className={classes.actionsRow}>{actions?.map(renderAction)}</div>
      )}
    </div>
  );
}

export default Snackbar;
